import {
  AddOutlined,
  DownloadOutlined,
  PageDS,
  Spacer,
  TabMenu,
  ToastContainerDS,
  triggerToastDS,
} from "@qivia/ui";
import { isThisMonth } from "date-fns";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompany, selectManager } from "../homeSlice";
import { formatDateWithoutDay } from "../../../libSlice/materialUiFormats";
import "../../../i18n/i18n";
import { SummaryTab } from "./summaryTab";
import { ChargingTrackingTab } from "./chargingTrackingTab";
import { SupportingDocumentsTab } from "./supportingDocumentsTab";
import { LinkyConnectionsTab } from "./linkyConnectionsTab";
import { HomeChargingDriversModal } from "./modals/homeChargingDriversModal";
import { ElectricVehiclesTab } from "./electricVehiclesTab";
import { ReimburseChargesModal } from "./reimburseChargesModal";
import { CreateSupportingDocumentsModal } from "./modals/createSupportingDocumentsModal";
import { isElectricChargingEnabled } from "./electricChargingAccess";
import {
  homeChargingSupportingDocumentsSlice,
  selectCreateHomeChargingSupportingDocumentsStatus,
  selectCreatedHomeChargingSupportingDocumentsCount,
  selectHomeChargingSupportingDocuments,
  selectHomeChargingSupportingDocumentsPeriod,
} from "./homeChargingSupportingDocumentsSlice";

export const ElectricCharging = (props: {
  basePath: "/home" | "/departments";
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const manager = useAppSelector(selectManager);
  const company = useAppSelector(selectCompany);
  const supportingDocuments = useAppSelector(
    selectHomeChargingSupportingDocuments,
  );
  const supportingDocumentsPeriod = useAppSelector(
    selectHomeChargingSupportingDocumentsPeriod,
  );
  const createSupportingDocumentsStatus = useAppSelector(
    selectCreateHomeChargingSupportingDocumentsStatus,
  );
  const createdSupportingDocumentsCount = useAppSelector(
    selectCreatedHomeChargingSupportingDocumentsCount,
  );
  const { basePath } = props;

  const selectedTab = params.tab ?? "summary";
  const isOwnerOrAdmin = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );
  const [isHomeChargingDriversModalOpen, setIsHomeChargingDriversModalOpen] =
    useState<boolean>(false);
  const [isReimburseModalOpen, setIsReimburseModalOpen] =
    useState<boolean>(false);
  const [
    isCreateSupportingDocumentsModalOpen,
    setIsCreateSupportingDocumentsModalOpen,
  ] = useState<boolean>(false);

  const tabs = useMemo(
    () => [
      {
        name: "summary",
        title: t("electric_charging.tab.summary"),
      },
      {
        name: "charging_tracking",
        title: t("electric_charging.tab.charging_tracking"),
      },
      ...(import.meta.env["VITE_TARGET_ENV"] !== "production"
        ? [
            {
              name: "supporting_documents",
              title: t("electric_charging.tab.supporting_documents"),
            },
          ]
        : []),
      {
        name: "electric_vehicles",
        title: t("electric_charging.tab.electric_vehicles"),
      },
      {
        name: "linky_connections",
        title: t("electric_charging.tab.linky_connections"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (basePath === "/home" && !company) return;
    const companyUuid = basePath === "/home" ? company?.uuid : undefined;
    if (!isElectricChargingEnabled(companyUuid)) {
      navigate(basePath);
    }
  }, [navigate, basePath, company]);

  useEffect(() => {
    if (!params.tab) {
      navigate(`${basePath}/electric_charging/summary`, { replace: true });
    }
  }, [params.tab, basePath, navigate]);

  useEffect(() => {
    if (
      import.meta.env["VITE_TARGET_ENV"] === "production" &&
      selectedTab === "supporting_documents"
    ) {
      navigate(`${basePath}/electric_charging/summary`, { replace: true });
    }
  }, [selectedTab, basePath, navigate]);

  useEffect(() => {
    if (
      createSupportingDocumentsStatus === "success" &&
      createdSupportingDocumentsCount
    ) {
      triggerToastDS(
        t("home_charging_supporting_documents.create.success", {
          count: createdSupportingDocumentsCount,
          period: supportingDocumentsPeriod
            ? formatDateWithoutDay(supportingDocumentsPeriod.from)
            : "",
        }),
        "SUCCESS",
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetCreateHomeChargingSupportingDocumentsStatus(),
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetHomeChargingSupportingDocumentsStatus(),
      );
      setIsCreateSupportingDocumentsModalOpen(false);
    } else if (createSupportingDocumentsStatus === "failed") {
      triggerToastDS(
        t("home_charging_supporting_documents.create.error"),
        "ERROR",
      );
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.resetCreateHomeChargingSupportingDocumentsStatus(),
      );
    }
  }, [
    dispatch,
    createSupportingDocumentsStatus,
    createdSupportingDocumentsCount,
    supportingDocumentsPeriod,
    t,
  ]);

  const canAddHomeChargingDrivers = useMemo(
    () => selectedTab === "linky_connections" && isOwnerOrAdmin,
    [selectedTab, isOwnerOrAdmin],
  );

  const canValidateReimbursements = useMemo(
    () => selectedTab === "charging_tracking" && isOwnerOrAdmin,
    [selectedTab, isOwnerOrAdmin],
  );

  const canManageSupportingDocuments = useMemo(
    () => selectedTab === "supporting_documents" && isOwnerOrAdmin,
    [selectedTab, isOwnerOrAdmin],
  );

  const isSupportingDocumentsCurrentMonth = useMemo(
    () =>
      !!supportingDocumentsPeriod &&
      isThisMonth(new Date(supportingDocumentsPeriod.from)),
    [supportingDocumentsPeriod],
  );

  const isCreateSupportingDocumentsDisabled = useMemo(
    () =>
      isSupportingDocumentsCurrentMonth ||
      !supportingDocuments.some(
        (supportingDocument) =>
          supportingDocument.chargesCount > 0 &&
          supportingDocument.supportingDocument === null,
      ),
    [isSupportingDocumentsCurrentMonth, supportingDocuments],
  );

  const isDownloadSupportingDocumentsDisabled = useMemo(
    () =>
      isSupportingDocumentsCurrentMonth ||
      !supportingDocuments.some(
        (supportingDocument) => supportingDocument.supportingDocument,
      ),
    [isSupportingDocumentsCurrentMonth, supportingDocuments],
  );

  const cta = useMemo(() => {
    if (canValidateReimbursements) {
      return {
        title: t("electric_charging.reimburseModal.open"),
        action: () => {
          setIsReimburseModalOpen(true);
        },
        width: 16,
      };
    }
    if (canAddHomeChargingDrivers) {
      return {
        title: t("linky_consents.cta.addDrivers"),
        action: () => {
          setIsHomeChargingDriversModalOpen(true);
        },
        width: 14,
        leftIcon: <AddOutlined />,
      };
    }
    if (canManageSupportingDocuments) {
      return {
        title: t("home_charging_supporting_documents.cta.create"),
        action: () => {
          if (!isCreateSupportingDocumentsDisabled)
            setIsCreateSupportingDocumentsModalOpen(true);
        },
        isDisabled: isCreateSupportingDocumentsDisabled,
      };
    }
    return undefined;
  }, [
    canValidateReimbursements,
    canAddHomeChargingDrivers,
    canManageSupportingDocuments,
    isCreateSupportingDocumentsDisabled,
    t,
  ]);

  const cta2 = useMemo(
    () =>
      canManageSupportingDocuments
        ? {
            title: t("home_charging_supporting_documents.cta.download"),
            action: () => {
              if (!isDownloadSupportingDocumentsDisabled)
                console.log(supportingDocumentsPeriod);
            },
            isDisabled: isDownloadSupportingDocumentsDisabled,
            leftIcon: <DownloadOutlined />,
          }
        : undefined,
    [
      canManageSupportingDocuments,
      isDownloadSupportingDocumentsDisabled,
      supportingDocumentsPeriod,
      t,
    ],
  );

  return (
    <PageDS
      title={t("electric_charging.title")}
      cta={cta}
      cta2={cta2}
      toaster={<ToastContainerDS />}
      topElement={
        <>
          <Spacer y={1} />
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={(tab) =>
              navigate(`${basePath}/electric_charging/${tab}`)
            }
          />
          <Spacer y={1.5} />
        </>
      }
      supportButton={{
        showMail: true,
      }}
    >
      <SummaryTab />
      <ChargingTrackingTab />
      <SupportingDocumentsTab />
      <ElectricVehiclesTab />
      <LinkyConnectionsTab />
      <HomeChargingDriversModal
        visible={canAddHomeChargingDrivers && isHomeChargingDriversModalOpen}
        onCloseModal={() => {
          setIsHomeChargingDriversModalOpen(false);
        }}
      />
      <ReimburseChargesModal
        isVisible={isReimburseModalOpen}
        onClose={() => setIsReimburseModalOpen(false)}
      />
      <CreateSupportingDocumentsModal
        isVisible={
          canManageSupportingDocuments && isCreateSupportingDocumentsModalOpen
        }
        onClose={() => setIsCreateSupportingDocumentsModalOpen(false)}
      />
    </PageDS>
  );
};
